import { useRef, useState } from "react";
import { Copy, ImagePlus, RotateCcw, Save, Trash2 } from "lucide-react";
import { mediaUrl } from "@/content/ContentContext";
import { apiUploadMedia } from "@/admin/adminApi";

export const getPath = (obj, path) =>
  path.reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj);

export const setPath = (obj, path, value) => {
  if (path.length === 0) {
    return value;
  }
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  clone[head] = setPath(obj ? obj[head] : undefined, rest, value);
  return clone;
};

export const TextField = ({ label, node, onChange, multiline = false, testId }) => {
  const value = node || { text: "", size: null };
  return (
    <div className="af-field" data-testid={testId}>
      <div className="af-field-head">
        <span className="af-label">{label}</span>
        <span className="af-size">
          <input
            type="number"
            min="6"
            max="200"
            placeholder="auto"
            value={value.size ?? ""}
            onChange={(e) =>
              onChange({ ...value, size: e.target.value ? Number(e.target.value) : null })
            }
            aria-label={`${label} font size in px`}
          />
          <em>px</em>
        </span>
      </div>
      {multiline ? (
        <textarea
          className="af-input af-textarea"
          rows={3}
          value={value.text}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      ) : (
        <input
          className="af-input"
          type="text"
          value={value.text}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      )}
    </div>
  );
};

export const PlainField = ({ label, value, onChange, placeholder, testId }) => (
  <div className="af-field" data-testid={testId}>
    <div className="af-field-head">
      <span className="af-label">{label}</span>
    </div>
    <input
      className="af-input"
      type="text"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// Module-level so the inputs keep their identity across parent re-renders.
// Defining these inside a tab component remounts the input on every keystroke
// and the caret loses focus.
export const NodeField = ({ content, update, label, path, multiline }) => (
  <TextField
    label={label}
    node={getPath(content, path)}
    multiline={multiline}
    onChange={(value) => update(path, value)}
  />
);

export const PlainNodeField = ({ content, update, label, path, placeholder }) => (
  <PlainField
    label={label}
    value={getPath(content, path)}
    placeholder={placeholder}
    onChange={(value) => update(path, value)}
  />
);

export const ListField = ({ label, items, onChange, hint }) => (
  <div className="af-field">
    <div className="af-field-head">
      <span className="af-label">{label}</span>
      {hint && <span className="af-hint">{hint}</span>}
    </div>
    <textarea
      className="af-input af-textarea"
      rows={Math.max(3, items.length + 1)}
      value={items.join("\n")}
      onChange={(e) => onChange(e.target.value.split("\n"))}
      onBlur={(e) => onChange(e.target.value.split("\n").filter((line) => line.trim() !== ""))}
    />
  </div>
);

export const ParagraphsField = ({ label, items, onChange, hint }) => (
  <div className="af-field">
    <div className="af-field-head">
      <span className="af-label">{label}</span>
      <span className="af-hint">{hint || "Separate paragraphs with a blank line"}</span>
    </div>
    <textarea
      className="af-input af-textarea af-textarea-tall"
      rows={10}
      value={items.join("\n\n")}
      onChange={(e) => onChange(e.target.value.split(/\n\s*\n/))}
      onBlur={(e) =>
        onChange(
          e.target.value
            .split(/\n\s*\n/)
            .map((part) => part.trim())
            .filter(Boolean)
        )
      }
    />
  </div>
);

export const ImageField = ({ label, value, onChange, defaultNote, testId }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is larger than 5MB");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await apiUploadMedia(file);
      onChange(res.data.url);
    } catch (err) {
      setError("Upload failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="af-field" data-testid={testId}>
      <div className="af-field-head">
        <span className="af-label">{label}</span>
      </div>
      <div className="af-image-row">
        <div className="af-image-preview">
          {value ? (
            <img src={mediaUrl(value)} alt={label} />
          ) : (
            <span className="af-image-default">{defaultNote || "Using default image"}</span>
          )}
        </div>
        <div className="af-image-actions">
          <button
            type="button"
            className="af-btn"
            disabled={busy}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            <ImagePlus size={13} />
            {busy ? "Uploading..." : "Upload image"}
          </button>
          {value && (
            <button type="button" className="af-btn af-btn-ghost" onClick={() => onChange("")}>
              <RotateCcw size={13} />
              Reset to default
            </button>
          )}
          {error && <span className="af-error">{error}</span>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </div>
    </div>
  );
};

export const SaveBar = ({ tabId, saving, dirty, message, onSave }) => (
  <div className="af-savebar" data-testid={`admin-savebar-${tabId}`}>
    {message && <span className="af-message" data-testid={`admin-save-message-${tabId}`}>{message}</span>}
    {!message && dirty && <span className="af-dirty" data-testid={`admin-unsaved-${tabId}`}>Unsaved changes</span>}
    <button
      type="button"
      className="af-btn af-btn-primary"
      onClick={onSave}
      disabled={saving || !dirty}
      data-testid={`admin-save-btn-${tabId}`}
    >
      <Save size={14} />
      {saving ? "Saving..." : "Save Changes"}
    </button>
  </div>
);

export const Group = ({ title, children, open = false }) => (
  <details className="af-group" open={open}>
    <summary className="af-group-title">{title}</summary>
    <div className="af-group-body">{children}</div>
  </details>
);

export { Copy, Trash2 };
